import { supabase } from '../../shared/supabase/supabaseClient';

export type OrganizationAccessGrantRole = 'auditor' | 'contador';

export type OrganizationAccessGrant = {
  id: string;
  role: OrganizationAccessGrantRole;
  grantee_email: string | null;
  grantee_auth_user_id: string;
  expires_at: string | null;
  created_at: string | null;
  note: string | null;
};

function parseGrant(raw: Record<string, unknown>): OrganizationAccessGrant | null {
  const id = typeof raw.id === 'string' ? raw.id : null;
  const role = raw.role === 'auditor' || raw.role === 'contador' ? raw.role : null;
  const granteeId =
    typeof raw.grantee_auth_user_id === 'string' ? raw.grantee_auth_user_id : null;
  if (!id || !role || !granteeId) return null;
  return {
    id,
    role,
    grantee_email: typeof raw.grantee_email === 'string' ? raw.grantee_email : null,
    grantee_auth_user_id: granteeId,
    expires_at: typeof raw.expires_at === 'string' ? raw.expires_at : null,
    created_at: typeof raw.created_at === 'string' ? raw.created_at : null,
    note: typeof raw.note === 'string' ? raw.note : null,
  };
}

export async function listOrganizationAccessGrants(
  organizationId: string,
): Promise<OrganizationAccessGrant[]> {
  const { data, error } = await supabase.rpc('list_organization_access_grants', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  const grantsRaw =
    data != null && typeof data === 'object' && Array.isArray((data as { grants?: unknown }).grants)
      ? ((data as { grants: unknown[] }).grants)
      : [];
  return grantsRaw
    .map((g) => (g != null && typeof g === 'object' ? parseGrant(g as Record<string, unknown>) : null))
    .filter((g): g is OrganizationAccessGrant => g != null);
}

export async function grantOrganizationAccess(input: {
  organizationId: string;
  email: string;
  role: OrganizationAccessGrantRole;
  expiresAt?: string | null;
  note?: string | null;
}): Promise<{ grantId: string | null }> {
  const { data, error } = await supabase.rpc('grant_organization_access', {
    p_organization_id: input.organizationId,
    p_grantee_email: input.email.trim(),
    p_role: input.role,
    p_expires_at: input.expiresAt ?? null,
    p_note: input.note ?? null,
  });
  if (error) {
    const msg = error.message || '';
    if (/grantee_user_not_found/i.test(msg)) {
      throw new Error('Esa cuenta todavía no existe en Vuelto. Pedile que se registre primero.');
    }
    if (/cannot_grant_self/i.test(msg)) {
      throw new Error('No podés darte acceso a vos mismo.');
    }
    if (/invalid_grant_role/i.test(msg)) {
      throw new Error('El rol tiene que ser auditor o contador.');
    }
    if (/organization_access_denied|not_authenticated/i.test(msg)) {
      throw new Error('Solo el dueño puede dar este acceso.');
    }
    throw new Error(msg || 'No pudimos dar el acceso.');
  }
  const grantId =
    data != null && typeof data === 'object' && typeof (data as { grant_id?: unknown }).grant_id === 'string'
      ? ((data as { grant_id: string }).grant_id)
      : null;
  return { grantId };
}

export async function revokeOrganizationAccess(grantId: string): Promise<void> {
  const { error } = await supabase.rpc('revoke_organization_access', {
    p_grant_id: grantId,
  });
  if (error) {
    if (/organization_access_denied|not_authenticated/i.test(error.message)) {
      throw new Error('Solo el dueño puede quitar este acceso.');
    }
    throw new Error(error.message || 'No pudimos quitar el acceso.');
  }
}
